import React, { useState, useEffect } from 'react';
import {
  Box,
  Tabs,
  Tab,
  Typography,
  TextField,
  Button,
  FormControlLabel,
  Switch,
  MenuItem,
  Paper,
  Snackbar,
  Alert,
  CircularProgress
} from '@mui/material';
import { featureManagementService } from '../services/featureManagement.service';

const FeatureFlags = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Module registration form state
  const [moduleForm, setModuleForm] = useState({
    developer_id: '',
    module_name: '',
    module_code: '',
    description: '',
    is_active: true
  });

  // Feature registration form state
  const [featureForm, setFeatureForm] = useState({
    developer_id: '',
    module_code: '',
    feature_name: '',
    feature_code: '',
    description: ''
  });

  // Load modules on component mount
  useEffect(() => {
    loadModules();
  }, []);

  const loadModules = async () => {
    try {
      setLoading(true);
      const response = await featureManagementService.getModules();
      setModules(response.modules);
    } catch (err) {
      setError('Failed to load modules: ' + (err.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleModuleFormChange = (event) => {
    const { name, value, checked } = event.target;
    setModuleForm(prev => ({
      ...prev,
      [name]: name === 'is_active' ? checked : value
    }));
  };

  const handleFeatureFormChange = (event) => {
    const { name, value } = event.target;
    setFeatureForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleModuleSubmit = async (event) => {
    event.preventDefault();
    try {
      setLoading(true);
      await featureManagementService.registerModule(moduleForm);
      setSuccess('Module registered successfully');
      setModuleForm({
        developer_id: '',
        module_name: '',
        module_code: '',
        description: '',
        is_active: true
      });
      loadModules(); // Reload modules after successful registration
    } catch (err) {
      setError('Failed to register module: ' + (err.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const handleFeatureSubmit = async (event) => {
    event.preventDefault();
    try {
      setLoading(true);
      await featureManagementService.registerFeature(featureForm);
      setSuccess('Feature registered successfully');
      setFeatureForm({
        developer_id: '',
        module_code: '',
        feature_name: '',
        feature_code: '',
        description: ''
      });
    } catch (err) {
      setError('Failed to register feature: ' + (err.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setError(null);
    setSuccess(null);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Feature Flags Management
      </Typography>

      <Paper sx={{ mb: 3 }}>
        <Tabs value={activeTab} onChange={handleTabChange}>
          <Tab label="Register Module" />
          <Tab label="Register Feature" />
        </Tabs>
      </Paper>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
          <CircularProgress />
        </Box>
      )}

      {activeTab === 0 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Register New Module
          </Typography>
          <form onSubmit={handleModuleSubmit}>
            <TextField
              fullWidth
              label="Developer ID"
              name="developer_id"
              value={moduleForm.developer_id}
              onChange={handleModuleFormChange}
              margin="normal"
              required
            />
            <TextField
              fullWidth
              label="Module Name"
              name="module_name"
              value={moduleForm.module_name}
              onChange={handleModuleFormChange}
              margin="normal"
              required
            />
            <TextField
              fullWidth
              label="Module Code"
              name="module_code"
              value={moduleForm.module_code}
              onChange={handleModuleFormChange}
              margin="normal"
              required
            />
            <TextField
              fullWidth
              label="Description"
              name="description"
              value={moduleForm.description}
              onChange={handleModuleFormChange}
              margin="normal"
              multiline
              rows={3}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={moduleForm.is_active}
                  onChange={handleModuleFormChange}
                  name="is_active"
                />
              }
              label="Active"
            />
            <Box sx={{ mt: 2 }}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={loading}
              >
                Register Module
              </Button>
            </Box>
          </form>
        </Paper>
      )}

      {activeTab === 1 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Register New Feature
          </Typography>
          <form onSubmit={handleFeatureSubmit}>
            <TextField
              fullWidth
              label="Developer ID"
              name="developer_id"
              value={featureForm.developer_id}
              onChange={handleFeatureFormChange}
              margin="normal"
              required
            />
            <TextField
              fullWidth
              select
              label="Module"
              name="module_code"
              value={featureForm.module_code}
              onChange={handleFeatureFormChange}
              margin="normal"
              required
            >
              {modules.map((module) => (
                <MenuItem key={module[0]} value={module[2]}>
                  {module[1]} ({module[2]})
                </MenuItem>
              ))}
            </TextField>
            <TextField
              fullWidth
              label="Feature Name"
              name="feature_name"
              value={featureForm.feature_name}
              onChange={handleFeatureFormChange}
              margin="normal"
              required
            />
            <TextField
              fullWidth
              label="Feature Code"
              name="feature_code"
              value={featureForm.feature_code}
              onChange={handleFeatureFormChange}
              margin="normal"
              required
            />
            <TextField
              fullWidth
              label="Description"
              name="description"
              value={featureForm.description}
              onChange={handleFeatureFormChange}
              margin="normal"
              multiline
              rows={3}
            />
            <Box sx={{ mt: 2 }}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={loading}
              >
                Register Feature
              </Button>
            </Box>
          </form>
        </Paper>
      )}

      <Snackbar
        open={!!error || !!success}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={error ? 'error' : 'success'}
          sx={{ width: '100%' }}
        >
          {error || success}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default FeatureFlags; 